import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Lock, Gift, CheckCircle, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { aboutContent, siteConfig } from '@/data/mock';

const About = () => {
  const getIcon = (title) => {
    const icons = {
      'Verständnis': Heart,
      'Vertraulichkeit': Lock,
      'Kostenlos': Gift,
    };
    const Icon = icons[title] || Heart;
    return <Icon className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)] pattern-bg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
              {aboutContent.subtitle}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(215,25%,15%)] mb-6">
              {aboutContent.title}
            </h1>
            <p className="text-xl text-[hsl(215,15%,45%)] leading-relaxed">
              Ich bin kein Anwalt, kein Therapeut und kein Berater. Ich bin ein Vater, 
              der selbst durch diese Hölle gegangen ist – und heute anderen helfen möchte.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Story Content */}
            <div>
              <Quote className="w-12 h-12 text-[hsl(213,80%,35%,0.3)] mb-6" />
              <div className="prose prose-lg">
                {aboutContent.story.split('\n\n').map((paragraph, index) => (
                  <p 
                    key={index} 
                    className="text-[hsl(215,25%,30%)] leading-relaxed mb-6 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Values Cards */}
            <div className="space-y-6">
              {aboutContent.values.map((value, index) => (
                <Card 
                  key={index} 
                  className="card-hover border-[hsl(210,25%,90%)] bg-white"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mr-4 flex-shrink-0">
                        {getIcon(value.title)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[hsl(215,25%,15%)] mb-2">
                          {value.title}
                        </h3>
                        <p className="text-[hsl(215,15%,45%)]">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What I Can & Cannot Do */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Was ich für Sie tun kann
            </h2>
            <p className="text-lg text-[hsl(215,15%,45%)] max-w-2xl mx-auto">
              Transparenz ist mir wichtig. Hier erfahren Sie genau, was Sie erwarten können.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Can Do */}
            <Card className="border-[hsl(160,60%,80%)] bg-[hsl(160,60%,98%)]">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-[hsl(160,60%,40%)] text-white flex items-center justify-center mr-3">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-semibold text-[hsl(160,60%,25%)]">Das kann ich</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Zuhören und Ihre Situation verstehen',
                    'Praktische Tipps aus eigener Erfahrung geben',
                    'Kontakte zu spezialisierten Anwälten vermitteln',
                    'Bei der Vorbereitung auf Behördentermine helfen',
                    'Emotionale Unterstützung bieten',
                    'Sie mit anderen Betroffenen vernetzen'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[hsl(160,60%,40%)] mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-[hsl(160,60%,25%)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Cannot Do */}
            <Card className="border-[hsl(35,70%,80%)] bg-[hsl(35,90%,98%)]">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-[hsl(35,90%,50%)] text-white flex items-center justify-center mr-3">
                    <span className="text-xl font-bold">!</span>
                  </div>
                  <h3 className="text-xl font-semibold text-[hsl(35,50%,30%)]">Das kann ich nicht</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Rechtsberatung oder rechtliche Vertretung',
                    'Therapeutische oder psychologische Beratung',
                    'Garantien für bestimmte Ergebnisse',
                    'Behördliche Entscheidungen beeinflussen',
                    'Schriftliche rechtliche Stellungnahmen',
                    'Vor Gericht auftreten'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-5 h-5 rounded-full bg-[hsl(35,90%,50%,0.3)] flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 text-[hsl(35,90%,40%)] text-xs font-bold">
                        ×
                      </span>
                      <span className="text-[hsl(35,50%,30%)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-6">
            Bereit für ein Gespräch?
          </h2>
          <p className="text-lg text-[hsl(215,15%,45%)] mb-8 max-w-2xl mx-auto">
            Der erste Schritt kostet Überwindung. Aber manchmal ist es genau dieser Schritt, 
            der alles verändert. Ich bin hier, um zuzuhören.
          </p>
          <Button asChild size="lg" className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white shadow-lg hover:shadow-xl transition-all text-base px-8">
            <Link to="/kontakt">
              Kontakt aufnehmen
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
